/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

/**
 * Core forecasting algorithm interface for financial time series prediction
 * Used by Moving Average and Exponential Smoothing implementations
 */
interface ForecastingAlgorithm {
    /**
     * Generate forecasts for given time series data
     * @param data Historical time series data (e.g., daily spending amounts)
     * @param periods Number of future periods to forecast
     * @return Forecasting result with predictions and metadata
     */
    fun forecast(data: List<Double>, periods: Int): ForecastingResult
}

/**
 * Result of a forecasting algorithm execution
 */
data class ForecastingResult(
    val algorithmName: String,
    val forecastValues: List<Double>,
    val confidence: Double, // 0.0 to 1.0
    val parameters: Map<String, Any>,
    val historicalValues: List<Double> = emptyList() // Computed historical smoothing values
)